import { Box, Grid } from '@mui/material';
import { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { Text } from '../pdf/layout';
import { compactFontSize } from '../pdf/pages/ResultsDetailedPage';
import { HumanizedValueWithUncertainty } from './SeedsTooltipComponent';

type PdfLegendProps = {
  children: ReactNode;
  compact?: boolean;
  color?: string;
};

export const PdfLegend = ({ children, compact, color }: PdfLegendProps) => {
  const { t } = useTranslation(['common', 'project', 'model']);
  return (
    <Grid
      container
      rowSpacing={1}
      sx={{ paddingTop: compact ? '0.4rem' : '2rem' }}
    >
      <Grid
        item
        xs={12}
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          paddingBottom: compact ? '0.1rem' : '1rem',
        }}
      >
        <Text
          fontSize={compact ? '1rem' : '1.5rem'}
          color={color ? color : 'black'}
          fontWeight={'bold'}
        >
          {t('project:results-page.legend').toLocaleUpperCase()}
        </Text>
      </Grid>
      {children}
    </Grid>
  );
};

interface PdfLegendRowProps {
  color: string | undefined;
  category: string | undefined;
  value?: string | undefined;
  unit: string | undefined;
  percent?: number;
  link?: string;
  uncertaintyRelative?: number | undefined;
  compact?: boolean;
  nbColumns?: number,
}

export const PdfLegendRow = ({
  color,
  category,
  nbColumns = 2,
  value,
  unit,
  percent,
  link,
  uncertaintyRelative,
  compact,
}: PdfLegendRowProps) => {
  const { t } = useTranslation(['common', 'project', 'model']);
  const fontSize = compact ? compactFontSize : '1rem';
  const fontWeight = compact ? 'lighter' : 'bold';
  return (
    <Grid container item xs={12 / nbColumns}>
      <Grid item xs={1}>
        <Box
          sx={{
            width: compact ? '0.6rem' : '1rem',
            height: compact ? '1.2rem' : '2rem',
            backgroundColor: color,
          }}
        />
      </Grid>
      <Grid
        item
        xs={11}
        style={{
          display: 'flex',
          justifyContent: 'flex-start',
          flexWrap: 'wrap',
          alignContent: 'center',
        }}
      >
        <Text fontWeight={fontWeight} fontSize={fontSize}>
          {category}&nbsp;:&nbsp;
        </Text>
        <Text fontSize={fontSize}>
          <HumanizedValueWithUncertainty
            value={value}
            uncertaintyRelative={uncertaintyRelative}
            unit={unit}
          />
        </Text>
        {percent && (
          <Text fontSize={fontSize}>
            , {t('common:or-percentage')}{' '}
            <b>{Math.round(Number(percent) * 100)}</b> %
          </Text>
        )}
      </Grid>
    </Grid>
  );
};

export default PdfLegend;
